import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CardsService } from '@app-services/cards.service';
import { UuidService } from '@app-services/uuid.service';
import { StatClick } from '@app-model/businessCard';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LinkTypeStatsClick } from '@app-model/linkTypeStatsClick';

export type ShareLinkTypeMap = Record<string, LinkTypeStatsClick>;

/**
 * Centralized service for tracking user interactions.
 *
 * Provides small helpers to reduce tracking boilerplate in components.
 *
 * Usage:
 * 1. `trackClick` directive (recommended in templates)
 * 2. `trackClick()` method (for TS logic)
 * 3. Helper methods (trackShare, trackFormSubmit, etc.)
 */
@Injectable({
	providedIn: 'root'
})
export class StatsTrackingService {
	constructor(private cardsService: CardsService, private uuidService: UuidService, private router: Router) {}

	static resolveShareLinkType(
		platform: string | undefined,
		mapping: ShareLinkTypeMap,
		fallback: LinkTypeStatsClick
	): LinkTypeStatsClick {
		if (!platform) {
			return fallback;
		}

		const normalized = platform.toLowerCase();
		return mapping[normalized] ?? fallback;
	}

	/**
	 * Main method to track any click event
	 *
	 * @param linkType Event type (LinkTypeStatsClick enum)
	 * @param linkUrl Optional destination URL
	 * @param elementId Optional element ID
	 * @param previousValue Optional previous value (for tracking changes)
	 * @param newValue Optional new value (for tracking changes)
	 * @returns Observable of the tracking request result
	 */
	trackClick(
		linkType: LinkTypeStatsClick,
		linkUrl?: string,
		elementId?: string,
		previousValue?: string | number | string[] | null,
		newValue?: string | number | string[] | null
	): Observable<any> {
		const uuid = this.uuidService.getUuid();
		const currentUrl = this.getCurrentCleanUrl();

		// Build payload including optional fields only when provided.
		const statsClick: StatClick = {
			linkType: linkType,
			linkUrl: typeof linkUrl !== 'undefined' ? linkUrl : null,
			elementId: elementId
		};

		if (typeof previousValue !== 'undefined') {
			(statsClick as any).previousValue = previousValue;
		}

		if (typeof newValue !== 'undefined') {
			(statsClick as any).newValue = newValue;
		}

		return this.cardsService
			.statsClick(uuid, currentUrl, statsClick)
			.pipe(tap({ error: err => console.error('Click event failed:', err) }));
	}

	/**
	 * Helper to track social shares
	 *
	 * @param platform Platform name (whatsapp, facebook, etc)
	 * @param url Shared URL
	 * @param context Context ('card' or 'property')
	 */
	trackShare(platform: string, url: string, context: 'card' | 'property' = 'card'): Observable<any> {
		const linkType = this.getShareLinkType(platform, context);
		return this.trackClick(linkType, url);
	}

	/**
	 * Helper to track form submissions
	 *
	 * @param formType Form type ('contact' or 'filter')
	 * @param formData Optional form data payload
	 */
	trackFormSubmit(formType: 'contact' | 'filter', formData?: any): Observable<any> {
		const linkType =
			formType === 'contact'
				? LinkTypeStatsClick.PROPERTY_CONTACT_FORM_SUBMIT
				: LinkTypeStatsClick.SEARCH_FILTER_SUBMIT;

		// Serialize form data as a JSON string (if provided)
		const linkUrl = formData ? JSON.stringify(formData) : undefined;

		return this.trackClick(linkType, linkUrl);
	}

	/**
	 * Helper to track toggles (accordion, modal, etc.)
	 *
	 * @param toggleType Toggle event type
	 * @param isOpen New state after toggle (open/closed)
	 */
	trackToggle(toggleType: LinkTypeStatsClick, isOpen: boolean): Observable<any> {
		return this.trackClick(toggleType, isOpen ? 'open' : 'close');
	}

	/**
	 * Helper to track clicks on promoted content
	 *
	 * @param contentType Promoted content type
	 * @param contentId Content ID
	 * @param destinationUrl Optional destination URL
	 */
	trackPromotedContent(contentType: LinkTypeStatsClick, contentId: string, destinationUrl?: string): Observable<any> {
		return this.trackClick(contentType, destinationUrl, contentId);
	}

	/**
	 * Helper to track video events
	 *
	 * @param videoType Video type ('youtube', 'youtube-list', 'hosted')
	 * @param action Action ('play', 'pause', 'ended')
	 * @param videoUrl Video URL
	 */
	trackVideo(
		videoType: 'youtube' | 'youtube-list' | 'hosted',
		action: 'play' | 'pause' | 'ended',
		videoUrl: string
	): Observable<any> {
		const linkType = this.getVideoLinkType(videoType, action);
		return this.trackClick(linkType, videoUrl);
	}

	/**
	 * Map platform + context to the proper share enum value
	 */
	private getShareLinkType(platform: string, context: 'card' | 'property'): LinkTypeStatsClick {
		const platformUpper = platform.toUpperCase();

		if (context === 'card') {
			// Card sharing
			switch (platformUpper) {
				case 'WHATSAPP':
					return LinkTypeStatsClick.CARD_SHARE_WHATSAPP;
				case 'FACEBOOK':
					return LinkTypeStatsClick.CARD_SHARE_FACEBOOK;
				case 'INSTAGRAM':
					return LinkTypeStatsClick.CARD_SHARE_INSTAGRAM;
				case 'TELEGRAM':
					return LinkTypeStatsClick.CARD_SHARE_TELEGRAM;
				case 'LINKEDIN':
					return LinkTypeStatsClick.CARD_SHARE_LINKEDIN;
				case 'TWITTER':
					return LinkTypeStatsClick.CARD_SHARE_TWITTER;
				case 'TIKTOK':
					return LinkTypeStatsClick.CARD_SHARE_TIKTOK;
				case 'COPY_URL':
					return LinkTypeStatsClick.CARD_SHARE_COPY_URL;
				default:
					return LinkTypeStatsClick.CARD_SHARE_BUTTON;
			}
		} else {
			// Property photo sharing
			switch (platformUpper) {
				case 'WHATSAPP':
					return LinkTypeStatsClick.PROPERTY_PHOTO_SHARE_WHATSAPP;
				case 'FACEBOOK':
					return LinkTypeStatsClick.PROPERTY_PHOTO_SHARE_FACEBOOK;
				case 'INSTAGRAM':
					return LinkTypeStatsClick.PROPERTY_PHOTO_SHARE_INSTAGRAM;
				case 'TELEGRAM':
					return LinkTypeStatsClick.PROPERTY_PHOTO_SHARE_TELEGRAM;
				case 'LINKEDIN':
					return LinkTypeStatsClick.PROPERTY_PHOTO_SHARE_LINKEDIN;
				case 'TWITTER':
					return LinkTypeStatsClick.PROPERTY_PHOTO_SHARE_TWITTER;
				case 'TIKTOK':
					return LinkTypeStatsClick.PROPERTY_PHOTO_SHARE_TIKTOK;
				case 'COPY_URL':
					return LinkTypeStatsClick.PROPERTY_PHOTO_COPY_URL;
				default:
					return LinkTypeStatsClick.PROPERTY_PHOTO_SHARE_BUTTON;
			}
		}
	}

	/**
	 * Map video type + action to the proper enum value
	 */
	private getVideoLinkType(videoType: string, action: string): LinkTypeStatsClick {
		switch (videoType) {
			case 'youtube': {
				switch (action) {
					case 'play':
						return LinkTypeStatsClick.PROMOTED_YOUTUBE_VIDEO_PLAY;
					case 'pause':
						return LinkTypeStatsClick.PROMOTED_YOUTUBE_VIDEO_PAUSE;
					case 'ended':
						return LinkTypeStatsClick.PROMOTED_YOUTUBE_VIDEO_ENDED;
					default:
						return LinkTypeStatsClick.MBC_ELEMENT;
				}
			}
			case 'youtube-list':
				return LinkTypeStatsClick.PROMOTED_YOUTUBE_LIST_VIDEO_PLAY;
			case 'hosted': {
				switch (action) {
					case 'play':
						return LinkTypeStatsClick.PROMOTED_HOSTED_VIDEO_PLAY;
					case 'pause':
						return LinkTypeStatsClick.PROMOTED_HOSTED_VIDEO_PAUSE;
					case 'ended':
						return LinkTypeStatsClick.PROMOTED_HOSTED_VIDEO_ENDED;
					default:
						return LinkTypeStatsClick.MBC_ELEMENT;
				}
			}
			default:
				return LinkTypeStatsClick.MBC_ELEMENT;
		}
	}

	/**
	 * Returns the current URL without query parameters
	 */
	private getCurrentCleanUrl(): string {
		const url = this.router.url;
		return url.includes('?') ? url.substring(0, url.indexOf('?')) : url;
	}
}
